import React, { createContext, useContext, useState, ReactNode } from 'react';
import { Toast, ToastContainer } from 'react-bootstrap';
import './NotificationContext.css';

interface NotificationContextProps {
  showNotification: (message: string, type?: 'success' | 'danger' | 'warning' | 'info') => void;
}

const NotificationContext = createContext<NotificationContextProps | undefined>(undefined);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification muss innerhalb eines NotificationProviders verwendet werden');
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

interface Notification {
  id: number;
  message: string;
  type: 'success' | 'danger' | 'warning' | 'info';
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [nextId, setNextId] = useState(1);

  const showNotification = (message: string, type: 'success' | 'danger' | 'warning' | 'info' = 'info') => {
    const id = nextId;
    setNextId(prevId => prevId + 1);

    setNotifications(prev => [...prev, { id, message, type }]);

    // Automatisch nach 5 Sekunden entfernen
    setTimeout(() => {
      setNotifications(prev => prev.filter(notification => notification.id !== id));
    }, 5000);
  };

  const handleClose = (id: number) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  };

  return (
    <NotificationContext.Provider value={{ showNotification }}>
      {children}

      <ToastContainer 
        position="top-center" 
        className="p-3" 
        style={{ 
          zIndex: 1070,
          position: 'fixed',
          top: '20%',
          left: '50%',
          transform: 'translateX(-50%)',
          maxWidth: '400px',
          width: '100%'
        }}>
        {notifications.map(notification => (
          <Toast 
            key={notification.id} 
            onClose={() => handleClose(notification.id)}
            bg={notification.type}
            delay={5000}
            autohide
            className="toast-notification"
          >
            <Toast.Header className="d-flex justify-content-between align-items-center">
              <strong className="me-auto text-center w-100">
                {notification.type === 'success' && 'Erfolg'}
                {notification.type === 'danger' && 'Fehler'}
                {notification.type === 'warning' && 'Warnung'}
                {notification.type === 'info' && 'Information'}
              </strong>
            </Toast.Header>
            <Toast.Body 
              className={`text-center ${notification.type === 'danger' || notification.type === 'warning' ? 'text-white' : ''}`}
              style={{ fontSize: '1.1rem', padding: '15px' }}
            >
              {notification.message}
            </Toast.Body>
          </Toast>
        ))}
      </ToastContainer>
    </NotificationContext.Provider>
  );
};
